// Lightweight Tawk.to integration helper for MasterEd.Front
// Loads the widget once, updates visitor/attributes, and allows hiding on unmount.

const TAWK_PROPERTY_ID = process.env.REACT_APP_TAWK_PROPERTY_ID || '68967e0addd4a01926704930';
const TAWK_WIDGET_ID = process.env.REACT_APP_TAWK_WIDGET_ID || '1j25tomta';
const TAWK_WIDGET_ID_TEST = process.env.REACT_APP_TAWK_WIDGET_ID_TEST || '';

let injected = false;
let injecting = false;
let lastPlacement = null; // remember last requested placement
let suppressBubble = false; // when true, keep the minimized bubble hidden

function ensureGlobals() {
  if (typeof window === 'undefined') return;
  if (!window.Tawk_API) window.Tawk_API = {};
  if (!window.Tawk_LoadStart) window.Tawk_LoadStart = new Date();
  // Ensure early hooks so suppression takes effect as soon as Tawk initializes
  try {
    if (!window.__tawkEarlyHooksInstalled) {
      const wrap = (key) => {
        const prev = window.Tawk_API[key];
        window.Tawk_API[key] = function () {
          if (typeof prev === 'function') {
            try { prev.apply(this, arguments); } catch {}
          }
          try { if (suppressBubble && window.Tawk_API.hideWidget) window.Tawk_API.hideWidget(); } catch {}
          // Perform any pending widget switch early
          try {
            if (window.__tawkPendingSwitchTo && window.Tawk_API.switchWidget) {
              const target = window.__tawkPendingSwitchTo;
              window.__tawkPendingSwitchTo = null;
              window.Tawk_API.switchWidget(TAWK_PROPERTY_ID, target);
            }
          } catch {}
        };
      };
      wrap('onBeforeLoad');
      wrap('onLoad');
      window.__tawkEarlyHooksInstalled = true;
    }
  } catch {}
}

export const isTawkTestWidgetConfigured = () => Boolean(TAWK_WIDGET_ID_TEST && TAWK_WIDGET_ID_TEST !== TAWK_WIDGET_ID);

export function switchToTestWidgetIfConfigured() {
  if (!isTawkTestWidgetConfigured()) return false;
  try {
    if (window.Tawk_API && window.Tawk_API.switchWidget) {
      window.Tawk_API.switchWidget(TAWK_PROPERTY_ID, TAWK_WIDGET_ID_TEST);
    } else {
      window.__tawkPendingSwitchTo = TAWK_WIDGET_ID_TEST;
    }
    return true;
  } catch {
    try { window.__tawkPendingSwitchTo = TAWK_WIDGET_ID_TEST; } catch {}
    return false;
  }
}

export function restoreDefaultWidgetIfNeeded() {
  if (!isTawkTestWidgetConfigured()) return false;
  try {
    if (window.Tawk_API && window.Tawk_API.switchWidget) {
      window.Tawk_API.switchWidget(TAWK_PROPERTY_ID, TAWK_WIDGET_ID);
    } else {
      window.__tawkPendingSwitchTo = TAWK_WIDGET_ID;
    }
    return true;
  } catch {
    try { window.__tawkPendingSwitchTo = TAWK_WIDGET_ID; } catch {}
    return false;
  }
}

export function resetTawkPlacement() {
  if (typeof window === 'undefined') return;
  lastPlacement = null;
  const clearStyles = (node) => {
    if (!node) return;
    try {
      node.style.removeProperty('bottom');
      node.style.removeProperty('top');
      node.style.removeProperty('left');
      node.style.removeProperty('right');
      node.style.removeProperty('position');
      node.style.removeProperty('transform');
      node.style.removeProperty('z-index');
    } catch {}
  };
  try {
    const container = document.getElementById('tawkchat-container');
    if (container) {
      clearStyles(container);
      const iframes = container.getElementsByTagName('iframe');
      if (iframes && iframes.length) Array.from(iframes).forEach(clearStyles);
      if (container.__tawkPlacementObserver) {
        try { container.__tawkPlacementObserver.disconnect(); } catch {}
        delete container.__tawkPlacementObserver;
      }
    }
  } catch {}
  try { clearInterval(window.__tawkPlacementInterval); } catch {}
}

export function initTawk({ attributes, visitor } = {}) {
  if (typeof window === 'undefined') return;
  ensureGlobals();

  // Set visitor early if possible
  if (visitor && window.Tawk_API) {
    try { window.Tawk_API.visitor = visitor; } catch {}
  }

  if (!injected && !injecting) {
    injecting = true;
    const s1 = document.createElement('script');
    const s0 = document.getElementsByTagName('script')[0];
    s1.async = true;
    s1.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s1.onload = () => {
      injected = true;
      injecting = false;
      // After load, apply attributes if provided
      if (attributes && window.Tawk_API && window.Tawk_API.setAttributes) {
        try { window.Tawk_API.setAttributes(attributes, function () {}); } catch {}
      }
      // Show widget if previously hidden
      if (window.Tawk_API) {
        try {
          if (suppressBubble) {
            window.Tawk_API.hideWidget();
          } else if (window.Tawk_API.showWidget) {
            window.Tawk_API.showWidget();
          }
        } catch {}
      }
    };
    try {
      s0.parentNode.insertBefore(s1, s0);
    } catch {
      // Fallback append to head
      (document.head || document.documentElement).appendChild(s1);
    }
  } else {
    // Already injected or in progress: push updates
    if (attributes && window.Tawk_API && window.Tawk_API.setAttributes) {
      try { window.Tawk_API.setAttributes(attributes, function () {}); } catch {}
    }
    if (window.Tawk_API) {
      try {
        if (suppressBubble) {
          window.Tawk_API.hideWidget();
        } else if (window.Tawk_API.showWidget) {
          window.Tawk_API.showWidget();
        }
      } catch {}
    }
  }
}

export function updateTawkAttributes(attributes = {}) {
  if (typeof window === 'undefined') return;
  if (window.Tawk_API && window.Tawk_API.setAttributes) {
    try { window.Tawk_API.setAttributes(attributes, function () {}); } catch {}
  }
}

export function hideTawk() {
  if (typeof window === 'undefined') return;
  if (window.Tawk_API) {
    try { if (window.Tawk_API.minimize) window.Tawk_API.minimize(); } catch {}
    try { if (window.Tawk_API.hideWidget) window.Tawk_API.hideWidget(); } catch {}
  }
}

export function setSuppressTawkBubble(enabled = true) {
  suppressBubble = !!enabled;
  if (typeof window === 'undefined') return;
  try {
    if (!window.Tawk_API) return;
    if (suppressBubble && window.Tawk_API.hideWidget) {
      window.Tawk_API.hideWidget();
    }
    if (!suppressBubble && window.Tawk_API.showWidget) {
      window.Tawk_API.showWidget();
    }
  } catch {}
}

export function openTawkChat() {
  if (typeof window === 'undefined') return;
  try {
    window.Tawk_API = window.Tawk_API || {};
    if (window.Tawk_API.showWidget) window.Tawk_API.showWidget();
    if (window.Tawk_API.maximize) window.Tawk_API.maximize();
  } catch {}
}

// Set widget placement offsets on demand.
// Example: setTawkPlacement({ right: 16, bottom: 100 }) or setTawkPlacement({ left: 16, bottom: 100 })
export function setTawkPlacement({ bottom, right, left, top } = {}) {
  if (typeof window === 'undefined') return;
  lastPlacement = { bottom, right, left, top };
  const styleTarget = (node) => {
    if (!node) return;
    try {
      const hasNum = (v) => typeof v === 'number';
      if (!lastPlacement || (!hasNum(lastPlacement.bottom) && !hasNum(lastPlacement.top) && !hasNum(lastPlacement.left) && !hasNum(lastPlacement.right))) {
        return;
      }
      // enforce fixed positioning so offsets apply
      node.style.setProperty('position', 'fixed', 'important');
      if (typeof lastPlacement.bottom === 'number') node.style.setProperty('bottom', `${lastPlacement.bottom}px`, 'important');
      if (typeof lastPlacement.top === 'number') node.style.setProperty('top', `${lastPlacement.top}px`, 'important');
      if (typeof lastPlacement.right === 'number') {
        node.style.setProperty('right', `${lastPlacement.right}px`, 'important');
        node.style.setProperty('left', 'auto', 'important');
      }
      if (typeof lastPlacement.left === 'number') {
        node.style.setProperty('left', `${lastPlacement.left}px`, 'important');
        node.style.setProperty('right', 'auto', 'important');
      }
      // reset opposite edges if not explicitly set
      if (typeof lastPlacement.top === 'number' && typeof lastPlacement.bottom !== 'number') node.style.setProperty('bottom', 'auto', 'important');
      if (typeof lastPlacement.bottom === 'number' && typeof lastPlacement.top !== 'number') node.style.setProperty('top', 'auto', 'important');
      // neutralize any transform-based positioning
      node.style.setProperty('transform', 'none', 'important');
      node.style.setProperty('z-index', '2147483647', 'important');
    } catch {}
  };
  // Observe document for Tawk nodes being (re)inserted
  try {
    if (!document.__tawkGlobalPlacementObserver) {
      const docMo = new MutationObserver(() => {
        const c = document.getElementById('tawkchat-container');
        if (c) {
          styleTarget(c);
          const ifr = c.getElementsByTagName('iframe');
          if (ifr && ifr.length) Array.from(ifr).forEach((f) => styleTarget(f));
        }
        const frames = document.querySelectorAll('iframe[src*="tawk.to"], iframe[id*="tawk"], iframe[name*="tawk"]');
        if (frames && frames.length) Array.from(frames).forEach((f) => styleTarget(f));
      });
      docMo.observe(document.documentElement, { childList: true, subtree: true });
      document.__tawkGlobalPlacementObserver = docMo;
    }
  } catch {}
  const apply = () => {
    const candidates = new Set();
    const pushAll = (nodes) => nodes && nodes.forEach ? nodes.forEach((n) => n && candidates.add(n)) : null;
    const qsa = (sel, root = document) => Array.from((root || document).querySelectorAll(sel));

    const container = document.getElementById('tawkchat-container');
    if (container) candidates.add(container);
    // Common wrapper ids/classes Tawk uses (minified / maximized)
    pushAll(qsa('[id*="tawkchat"], [class*="tawkchat"], [class*="tawk-chat"], [id^="tawk"]'));
    // Any iframes that belong to Tawk
    pushAll(qsa('iframe[src*="tawk"], iframe[id*="tawk"], iframe[name*="tawk"]'));

    if (candidates.size === 0) {
      setTimeout(apply, 300);
      return;
    }

    candidates.forEach((node) => styleTarget(node));

    // Observe primary container if present for internal changes that may reset inline styles
    if (container) {
      try {
        if (!container.__tawkPlacementObserver) {
          const mo = new MutationObserver(() => {
            candidates.forEach((node) => styleTarget(node));
          });
          mo.observe(container, { attributes: true, childList: true, subtree: true });
          container.__tawkPlacementObserver = mo;
        }
      } catch {}
    }
  };
  // Re-apply when widget finishes loading
  try {
    window.Tawk_API = window.Tawk_API || {};
    const prevOnLoad = window.Tawk_API.onLoad;
    window.Tawk_API.onLoad = function () {
      if (typeof prevOnLoad === 'function') {
        try { prevOnLoad(); } catch {}
      }
      apply();
      // If suppression is enabled, keep the minimized bubble hidden
      try { if (suppressBubble && window.Tawk_API.hideWidget) window.Tawk_API.hideWidget(); } catch {}
    };
    // Also re-apply on common state changes
    const wrap = (key) => {
      const prev = window.Tawk_API[key];
      window.Tawk_API[key] = function () {
        if (typeof prev === 'function') {
          try { prev.apply(this, arguments); } catch {}
        }
        apply();
        if (key === 'onChatMinimized') {
          // When user minimizes the chat, re-hide the minimized bubble if suppression is active
          try { if (suppressBubble && window.Tawk_API.hideWidget) window.Tawk_API.hideWidget(); } catch {}
        }
      };
    };
    ['onChatMinimized','onChatMaximized','onStatusChange'].forEach(wrap);
  } catch {}
  apply();
  // Fight style races by reapplying for a short burst
  try {
    clearInterval(window.__tawkPlacementInterval);
  } catch {}
  try {
    const started = Date.now();
    window.__tawkPlacementInterval = setInterval(() => {
      apply();
      if (Date.now() - started > 8000) {
        clearInterval(window.__tawkPlacementInterval);
      }
    }, 300);
  } catch {}
}
